import { getFullName, getShortName } from './entity-name-inputs';

export type MaybeEntityName =
    | {
          full?: string | null | undefined;
          short?: string | null | undefined;
      }
    | null
    | undefined;

/**
 * Resolves the name based on the given parameters.
 *
 * @param unknown name - The name object to extract the name from. If `null` or `undefined`, the fallback behavior is triggered.
 * @param {boolean} [short=false] - A flag indicating whether to use the short name or the full name. Default is `false`.
 * @param {string} fallback - The fallback value.
 * @returns {string} - The extracted name based on the provided parameters. If no name is available or the short name is null, the fallback behavior is applied.
 */
export function resolveName(
    nameInput?: unknown,
    short = false,
    fallback = '-- geen naam --'
): string {
    return (
        (short ? getShortName(nameInput) : getFullName(nameInput)) ?? fallback
    );
}

export function resolveNames(
    nameInputs?: unknown[] | null | undefined,
    short = false,
    fallback = '-- geen namen --'
): string[] {
    return (
        nameInputs?.map((nameInput) =>
            resolveName(nameInput, short, fallback)
        ) ?? []
    );
}

/**
 * @deprecated Use `resolveName()` without parameters.
 */
export function noName() {
    return '-- geen naam --';
}

/**
 * @deprecated Use `joinNames()` without parameters.
 */
export function noNames() {
    return '-- geen namen --';
}

/**
 * Joins names in a single string, seperated by comma's (',').
 * The last item is seperated by an ampersand ('&').
 * @param nameInputs the names or named nodes to join.
 * @param short whether the short name should be prefered.
 * @param fallback The fallback value when the inputs are empty.
 * @param initSeperator The seperator of the initial values.
 * @param endSeparator The separator of the end values.
 * @returns names joined in a single string
 */
export function joinNames(
    nameInputs?: unknown[] | null | undefined,
    short = false,
    fallback = '-- geen namen --',
    initSeperator = ', ',
    endSeperator = ' & '
) {
    if (!nameInputs || nameInputs.length === 0) return fallback;

    const filtered = nameInputs
        .map((nameInput) =>
            short ? getShortName(nameInput) : getFullName(nameInput)
        )
        .filter((name): name is string => typeof name === 'string');

    if (filtered.length === 0) return fallback;
    if (filtered.length === 1) return filtered[0];

    return (
        filtered.slice(0, -1).join(initSeperator) +
        endSeperator +
        filtered[filtered.length - 1]
    );
}

export type MaybeNamedNode =
    | {
          name?: MaybeEntityName;
      }
    | null
    | undefined;

/**
 * @deprecated Use resolveName instead.
 */
export const namedNodeToName = resolveName;

/**
 * @deprecated Use resolveNames instead.
 */
export const namedNodesToNames = resolveNames;
